/* eslint-disable react-refresh/only-export-components */
// This file intentionally exports both the Provider component and the useLanguage hook

import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LANGUAGES, detectBrowserLanguage, isRTL, type Language } from '../config/languages';
import { api } from '../services/api';

interface LanguageContextType {
  language: string;
  setLanguage: (code: string) => void;
  languages: Language[];
  isRTL: boolean;
  t: (key: string, fallback?: string, params?: Record<string, string | number>) => string;
  isLoadingStrings: boolean;
  isChangingLanguage: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'rumio_language';

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const queryClient = useQueryClient();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const [language, setLanguageState] = useState<string>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && LANGUAGES.some(l => l.code === stored)) {
        return stored;
      }
    }
    // Fall back to browser detection
    return detectBrowserLanguage();
  });

  // Fetch strings for current language
  const { data: strings, isLoading: isLoadingStrings } = useQuery({
    queryKey: ['strings', language],
    queryFn: async () => {
      const result = await api.getStrings(language, 'web');
      return result || {};
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update document attributes when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
  }, [language]);

  const setLanguage = useCallback(async (code: string) => {
    // Validate language code
    if (!LANGUAGES.some(l => l.code === code)) {
      console.warn(`Invalid language code: ${code}`);
      return;
    }

    // Skip if already this language
    if (code === language) return;

    console.log('[LanguageContext] Changing language to:', code);
    setIsChangingLanguage(true);

    try {
      // Prefetch new strings BEFORE updating state to prevent flash
      await queryClient.prefetchQuery({
        queryKey: ['strings', code],
        queryFn: () => api.getStrings(code, 'web'),
      });

      // Now update state - strings are already cached, so no flash
      setLanguageState(code);
      localStorage.setItem(STORAGE_KEY, code);
    } catch (error) {
      console.error('[LanguageContext] Failed to prefetch strings:', error);
      // Fall back to immediate switch if prefetch fails
      setLanguageState(code);
      localStorage.setItem(STORAGE_KEY, code);
    } finally {
      setIsChangingLanguage(false);
    }
  }, [queryClient, language]);

  // Translation function with optional interpolation
  const t = useCallback((key: string, fallback?: string, params?: Record<string, string | number>): string => {
    let result = strings?.[key] || fallback || key;
    // Handle interpolation: replace {{param}} with values
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
      });
    }
    return result;
  }, [strings]);

  const value = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage,
    languages: LANGUAGES,
    isRTL: isRTL(language),
    t,
    isLoadingStrings,
    isChangingLanguage,
  }), [language, setLanguage, t, isLoadingStrings, isChangingLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
